/**
 * SettingsScreen — Pal Reset, sign-out, and (later) consent ledger viewer.
 */

import { useState } from 'react';
import { api } from '../cloud/api';
import { useVoiceOptIn } from './hooks/useVoiceOptIn';
import type { UseThemePreferenceState } from './hooks/useThemePreference';
import { ThemeModeControl } from './ThemeModeControl';
import { VoiceOptInCard } from './VoiceOptInCard';

export function SettingsScreen({ onPalReset, onSignOut, themePreference }: {
    onPalReset: () => void;
    onSignOut: () => void;
    themePreference: UseThemePreferenceState;
}) {
    const [confirming, setConfirming] = useState(false);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const voiceOptIn = useVoiceOptIn();

    async function reset() {
        setBusy(true); setError(null);
        try {
            await api.palReset();
            onPalReset();
        } catch (err) {
            setError((err as Error).message);
        } finally {
            setBusy(false);
        }
    }

    async function signOut() {
        setBusy(true);
        try {
            await api.signOut();
            onSignOut();
        } catch { /* fall through */ }
        finally { setBusy(false); }
    }

    return (
        <div className="space-y-4">
            <ThemeModeControl hook={themePreference} />
            <VoiceOptInCard hook={voiceOptIn} />

            <div className="card">
                <h3 className="font-semibold mb-1">Sign out</h3>
                <p className="muted text-sm mb-3">
                    Clears the session cookie. Your Pal stays put — sign back in to pick up where you left off.
                </p>
                <button
                    className="btn-secondary"
                    onClick={signOut}
                    disabled={busy}
                >Sign out</button>
            </div>

            <div className="card border-danger/50">
                <h3 className="font-semibold text-danger mb-1">Reset your Pal</h3>
                <p className="muted text-sm mb-3">
                    Wipes your Pal's identity, vibe profile, and memory. The consent ledger is
                    preserved (compliance retention). You can onboard a fresh Pal afterwards.
                </p>
                {!confirming ? (
                    <button
                        className="btn-secondary text-danger border-danger/50"
                        onClick={() => setConfirming(true)}
                    >Reset Pal…</button>
                ) : (
                    <div className="flex gap-2">
                        <button
                            className="btn-secondary"
                            onClick={() => setConfirming(false)}
                            disabled={busy}
                        >Cancel</button>
                        <button
                            className="btn-primary bg-danger text-cockpit hover:bg-danger/90"
                            onClick={reset}
                            disabled={busy}
                        >{busy ? 'Resetting…' : 'Yes, reset'}</button>
                    </div>
                )}
                {error && <p className="text-danger text-xs mt-2">{error}</p>}
            </div>
        </div>
    );
}
